/**
 * One WhatsApp message, as it shipped.
 *
 * The truth of the thread is a Global Constraint: this renders `convo.messages` — for a template
 * the rendered body with its real params, never the model's draft — and the delivery state comes
 * from `delivery.delivery_status`, never a mutable copy. A failed message says WHY, using Meta's
 * own error text, because "failed" with no reason is the thing that wastes a rep's afternoon.
 *
 * Three kinds of outbound are visually distinct on purpose: the agent, a human rep, and an
 * approved template. A rep must be able to tell at a glance which of those the customer saw.
 *
 * Author: Danish Khan
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { DeliveryState, Message } from '../lib/types.ts';
import { formatTime } from '../lib/format.ts';
import { mediaUrl } from '../lib/media.ts';
import { Pill } from './Pill.tsx';

const DELIVERY_LABEL: Record<DeliveryState, string> = {
  queued: 'Queued — not sent to Meta yet',
  sending: 'Sending to Meta',
  sent: 'Sent to Meta',
  delivered: 'Delivered to the handset',
  read: 'Read by the customer',
  failed: 'Failed',
  deleted: 'Deleted by the sender',
  warning: 'Delivered with a warning from Meta',
};

function DeliveryTicks({ state }: { state: DeliveryState }): ReactNode {
  const label = DELIVERY_LABEL[state];
  const common = 'inline-flex items-center gap-0.5 text-[11px]';
  if (state === 'failed') {
    return (
      <span className={clsx(common, 'font-semibold text-danger')} title={label} aria-label={label} role="img">
        ✕ failed
      </span>
    );
  }
  if (state === 'deleted') {
    return (
      <span className={clsx(common, 'text-fg-subtle line-through')} title={label} aria-label={label} role="img">
        deleted
      </span>
    );
  }
  if (state === 'warning') {
    return (
      <span className={clsx(common, 'text-warn')} title={label} aria-label={label} role="img">
        ⚠ warning
      </span>
    );
  }
  if (state === 'queued' || state === 'sending') {
    return (
      <span className={clsx(common, 'opacity-60')} title={label} aria-label={label} role="img">
        ◷
      </span>
    );
  }
  const ticks = state === 'sent' ? '✓' : '✓✓';
  return (
    <span
      className={clsx(common, state === 'read' ? 'text-info font-semibold' : 'opacity-70')}
      title={label}
      aria-label={label}
      role="img"
    >
      {ticks}
    </span>
  );
}

export interface BubbleProps {
  message: Message;
  /**
   * What this tenant calls its agent — `session.tenant.agentName`, filled from the pack.
   *
   * Passed in rather than read from a context or defaulted to "Maya": a default would be this
   * tenant's name shown over a second tenant's replies, which is the failure the field exists to
   * prevent, and it would be invisible until somebody noticed the wrong name in a screenshot.
   */
  agentName: string;
  /** ops/manager only — opens the Explain panel for the turn that produced this bubble. */
  onExplain?: ((turnId: string) => void) | undefined;
}

export function Bubble({ message, agentName, onExplain }: BubbleProps): ReactNode {
  const inbound = message.direction === 'in';
  const isTemplate = message.kind === 'template' || message.template !== null;
  const isHuman = message.reason === 'human';

  const skin = inbound
    ? 'bg-bubble-in text-bubble-in-fg border border-line'
    : isHuman
      ? 'bg-bubble-human text-bubble-human-fg'
      : 'bg-bubble-out text-bubble-out-fg';

  return (
    <div
      className={clsx('flex w-full', inbound ? 'justify-start' : 'justify-end')}
      data-testid="bubble"
      data-direction={message.direction}
    >
      <div className={clsx('flex max-w-[min(34rem,82%)] flex-col gap-1')}>
        {!inbound && (
          <div className={clsx('flex items-center gap-1.5', 'justify-end')}>
            {isTemplate && (
              <Pill tone="info" title={`Approved template ${message.template?.name ?? ''} — sent outside the 24-hour window`}>
                Template · {message.template?.name ?? 'unnamed'}
              </Pill>
            )}
            {isHuman && <Pill tone="brand">{message.author ?? 'Human'}</Pill>}
            {!isHuman && !isTemplate && <Pill tone="neutral">{agentName}</Pill>}
          </div>
        )}

        <div className={clsx('rounded-[var(--h1-radius)] px-3 py-2 shadow-sm', skin)}>
          {message.media && message.media.length > 0 && (
            <div className={clsx('mb-2 grid gap-1', message.media.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
              {message.media.map((m) => (
                <img
                  key={m.id}
                  src={mediaUrl(m)}
                  alt=""
                  loading="lazy"
                  className="h-28 w-full rounded-md border border-line bg-surface-2 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ))}
            </div>
          )}

          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.body}</p>

          {message.template && (
            <p className="mt-1.5 border-t border-current/15 pt-1.5 font-mono text-[10px] opacity-70">
              {/*
                Built from the parts that exist. The API sends `language: ''` for every template
                (routes/inbox.ts never selects the column), and a template with no parameters is
                ordinary — so the fixed three-part string rendered "nudge_mini ·  · params: " on a
                real message, which reads as a missing field rather than an absent one.
              */}
              {[
                message.template.name,
                message.template.language || null,
                message.template.params.length > 0 ? `params: ${message.template.params.join(' | ')}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}

          <div className="mt-1 flex items-center justify-end gap-2 text-[11px] opacity-80">
            <time dateTime={message.at}>{formatTime(message.at)}</time>
            {!inbound && message.delivery && <DeliveryTicks state={message.delivery} />}
          </div>
        </div>

        {message.failureReason && (
          <p className="rounded-md bg-danger-soft px-2 py-1 text-[11px] text-danger-soft-fg" role="note">
            {message.failureReason}
          </p>
        )}

        {/*
          Outbound only, although the API fills `turnId` on BOTH sides of a turn: an inbound
          message carries the id of the turn it triggered, so the link would appear twice for the
          same panel — and "Why the agent said that" over a customer's own sentence is the wrong
          sentence. The reply is the thing being explained.
        */}
        {!inbound && message.turnId && onExplain && (
          <button
            type="button"
            onClick={() => onExplain(message.turnId as string)}
            className="self-end text-[11px] font-medium text-brand-ink underline underline-offset-2 hover:opacity-80"
          >
            Explain this turn
          </button>
        )}
      </div>
    </div>
  );
}
